// Copyright 2026, Imprivata, Inc.  All rights reserved.

import {
  checkConsent,
  getConsentLegalDocumentContent,
  submitConsent,
} from '@api/selfServiceServices';
import { ConsentType } from '@imprivata-cloud/authn';
import type { ConsentPorts, CreateConsentMachineArgs } from '@imprivata-cloud/consent';
import { useMemo } from 'react';
import type { ResidenceLocation, UserInfo } from '@/api/types';

type ConsentGrantType = 'granted' | 'not-granted' | '';

// The BFF returns grantType as a bare string; the consent module wants the
// narrower union. Guard rather than assert so an unexpected value degrades to
// "unknown" instead of lying to the state machine.
const normalizeConsentGrantType = (grantType: string): ConsentGrantType => {
  if (grantType === '' || grantType === 'granted' || grantType === 'not-granted') {
    return grantType;
  }
  return '';
};

interface BiometricConsentWiring {
  input: CreateConsentMachineArgs['input'];
  ports: ConsentPorts;
}

/**
 * Shared wiring for the `@imprivata-cloud/consent` module: the machine input and
 * the BFF-backed ports. Used by both first-time face enrollment (FaceCapture)
 * and biometric re-consent (UpdateBioAgreement, ME-11391) so the consent
 * implementation exists exactly once.
 */
export const useBiometricConsentPorts = (
  userInfo: UserInfo | undefined,
  residenceLocations: ResidenceLocation[] | undefined,
): BiometricConsentWiring => {
  const input = useMemo(
    () => ({
      consentType: ConsentType.BIOMETRICS,
      residenceLocations: residenceLocations ?? [],
      userUpn: userInfo?.userDisplayName ? userInfo.userDisplayName : userInfo?.userUpn,
    }),
    [residenceLocations, userInfo?.userDisplayName, userInfo?.userUpn],
  );

  const ports = useMemo<ConsentPorts>(
    () => ({
      checkConsent: async (checkInput: { consentType: string; userUpn?: string }) => {
        const response = await checkConsent(checkInput);
        return { ...response, grantType: normalizeConsentGrantType(response.grantType) };
      },
      submitConsent: async (submitInput: {
        grantType: ConsentGrantType;
        stateOfResidence: string;
        documentNameList: string[];
        userUpn?: string;
      }) =>
        submitConsent({
          consent: {
            consentType: ConsentType.BIOMETRICS,
            residenceLocationShortName: submitInput.stateOfResidence,
            grantType: submitInput.grantType,
            documentNameList: submitInput.documentNameList,
            userId: '',
          },
          userUpn: submitInput.userUpn,
        }),
      fetchDocument: getConsentLegalDocumentContent,
    }),
    [],
  );

  return { input, ports };
};
